import React, { Fragment, useCallback, useContext, useState } from "react";
import {
  Typography,
  Tooltip,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  styled,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Device } from "@services/devices/types";
import {
  getFilterLabel,
  useDeviceFilters,
} from "@common/utils/devices/aggregating/useDeviceFilters";
import { WithGrow } from "@common/components/WithGrow";
import { Priority } from "@services/common/types";
import Button, { PriorityButtons } from "@common/components/Button";
import { useTranslation } from "react-i18next";
import { Close, Replay } from "@mui/icons-material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { DeviceProvisioningDispatchContext } from "@pages/Assets/DeviceProvisioning/context";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canBulkChange } from "@common/utils/user";
import { AggregatedContext } from "@pages/Assets/Device/DetailView/Tabs/common/context";

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  "&.Mui-selected": {
    "&:hover": {
      cursor: "default",
      backgroundColor: `${theme.palette.action.active}`,
    },
  },
}));

// TODO make it more proper and improve performance

export const DeviceFilterBar = ({
  devices,
  refetch,
  provisionedOnly = true,
  setProvisionedOnly = () => {},
  isFromAggregator = false,
}: {
  devices?: Device[];
  refetch?: () => void | Promise<void>;
  provisionedOnly?: boolean;
  setProvisionedOnly?: (value: boolean) => void;
  isFromAggregator?: boolean;
}) => {
  const { filters, setFilters, resetFilters, filterFn } = useDeviceFilters();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const { setOpen } = useContext(DeviceProvisioningDispatchContext);
  const { setDevices = () => {} } = useContext(AggregatedContext) || {};
  const [user] = useAuthenticatedUser();
  const canBulk = canBulkChange(user);

  const handleRefetch = async () => {
    setLoading(true);
    if (refetch) await refetch();
    setLoading(false);
  };

  const handleProvisioning = (
    event: React.MouseEvent<HTMLElement>,
    toggleValue: boolean | null
  ) => {
    if (toggleValue !== null) {
      setDevices(undefined);
      setProvisionedOnly(toggleValue);
    }
  };

  const setOpenProvisioning = () => {
    setOpen(true);
  };

  const translateFilter = (k: keyof typeof filters) =>
    t(getFilterLabel(filters, k));

  const isFiltered = useCallback(
    () =>
      filters["status.reachable"] !== undefined ||
      filters["status.apiError"] !== undefined ||
      filters["status.loginError"] !== undefined ||
      filters["priority"].length > 0,
    [filters]
  );
  if (devices === undefined) return null;
  return (
    <WithGrow
      direction="row"
      sx={{ alignItems: "center", py: 1 }}
      fixed={
        <Grid container direction="row" sx={{ width: '100%', alignItems: "center" }}>
          <Typography>
            {`${
              devices?.filter(provisionedOnly ? filterFn : () => true)
                ?.length ?? 0
            } / ${devices?.length ?? 0} ${
              (devices?.length ?? 0) !== 1 ? t("Devices") : t("Device")
            }`}
          </Typography>
          {provisionedOnly && (
            <>
              <Divider
                sx={{ height: 28, mx: 1, borderRightWidth: 2 }}
                orientation="vertical"
              />
              <Typography>{t("Tickets")} -</Typography>
              <Grid sx={{ mx: 1, "& > .MuiButtonBase-root": { mx: 0.5 } }}>
                {[
                  Priority.Critical,
                  Priority.Major,
                  Priority.Minor,
                  Priority.Informational,
                ].map((value) => {
                  const Component = PriorityButtons[value];
                  return (
                    <Tooltip title={t(value) ?? ""} key={value} arrow>
                      <Component
                        size="small"
                        value={value}
                        selected={filters.priority.includes(value)}
                        onClick={() =>
                          setFilters((filters) => ({
                            ...filters,
                            priority: filters.priority.includes(value)
                              ? filters.priority.filter((x) => x !== value)
                              : [...filters.priority, value],
                          }))
                        }
                      />
                    </Tooltip>
                  );
                })}
              </Grid>
            </>
          )}
        </Grid>
      }
    >
      <WithGrow
        direction="row-reverse"
        fixedSx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
        fixed={
          <>
            {!provisionedOnly && devices && devices.length > 0 && canBulk && (
              <Button
                sx={{ mx: 0.5 }}
                startIcon={<CloudUploadOutlinedIcon />}
                ignoreLocalization
                onClick={setOpenProvisioning}
              >
                {t(
                  `Import {{count}} unprovisioned ${
                    devices.length === 1 ? "device" : "devices"
                  }`,
                  { count: devices.length }
                )}
              </Button>
            )}
            <ToggleButtonGroup
              sx={{
                mx: 1,
              }}
              size="small"
              value={provisionedOnly}
              exclusive
              onChange={handleProvisioning}
            >
              <StyledToggleButton value={true}>
                {t("Provisioned")}
              </StyledToggleButton>
              <StyledToggleButton value={false}>
                {t("Unprovisioned")}
              </StyledToggleButton>
            </ToggleButtonGroup>
            {isFromAggregator && (
              <Tooltip title={t("Refresh")} arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={loading}
                    onClick={handleRefetch}
                    sx={({ palette }) => ({
                      color: palette.text.primary,
                    })}
                  >
                    <Replay />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </>
        }
        containerSx={{ display: "flex" }}
      >
        {provisionedOnly && (
          <>
            {(
              [
                "status.reachable",
                "status.loginError",
                "status.apiError",
              ] as (keyof typeof filters)[]
            ).map((key) => {
              return filters[key] !== undefined ? (
                <Button
                  key={key}
                  color="white"
                  chip
                  endIcon={<Close />}
                  sx={{ mx: 0.5 }}
                  onClick={() =>
                    setFilters((filters) => ({
                      ...filters,
                      [key]: undefined,
                    }))
                  }
                >
                  {translateFilter(key)}
                </Button>
              ) : (
                <Fragment key={key} />
              );
            })}
            {provisionedOnly && isFiltered() && (
              <Button
                onClick={() => resetFilters()}
                variant="text"
                sx={(theme) => ({
                  color: theme.palette.common.grey.g828282,
                  mx: 1,
                })}
              >
                {t("Clear filters")}
              </Button>
            )}
          </>
        )}
      </WithGrow>
    </WithGrow>
  );
};
export default DeviceFilterBar;
